<script setup>
import { computed, ref, toRef } from 'vue'
import { useDynamicScroller } from 'vue-virtual-scroller/composables/useDynamicScroller'
import { RecycleScroller } from 'vue-virtual-scroller'

defineOptions({ inheritAttrs: false })

const props = defineProps({
  items: { type: Array, required: true },
  keyField: { default: 'id' },
  direction: { default: 'vertical' },
  listTag: { default: 'div' },
  itemTag: { default: 'div' },
  minItemSize: { type: [Number, String], required: true },
  shift: { type: Boolean, default: false },
  cache: { default: undefined },
  disableTransform: { type: Boolean, default: false },
  flowMode: { type: Boolean, default: false },
  hiddenPosition: { default: undefined },
  enabled: { type: Boolean, default: true },
  pageMode: { type: Boolean, default: false },
})

const emit = defineEmits(['resize', 'visible'])

const scroller = ref()
const items = toRef(props, 'items')

const scrollerEl = computed(() => {
  const exposedEl = scroller.value?.el
  if (exposedEl && typeof exposedEl === 'object' && 'value' in exposedEl) {
    return exposedEl.value
  }
  return exposedEl
})

const dynamicOptions = computed(() => ({
  items,
  keyField: props.keyField,
  direction: props.direction,
  minItemSize: props.minItemSize,
  shift: props.shift,
  cache: props.cache,
  flowMode: props.flowMode,
  hiddenPosition: props.hiddenPosition,
  enabled: props.enabled,
  pageMode: props.pageMode,
  el: scrollerEl.value,
  onResize: () => emit('resize'),
  onVisible: () => emit('visible'),
}))

const {
  itemsWithSize,
  forceUpdate,
  startSpacerSize,
  endSpacerSize,
  scrollToItem,
  scrollToPosition,
  findItemIndex,
  getItemOffset,
  getItemSize,
  cacheSnapshot,
  restoreCache,
  scrollToBottom,
  onScrollerResize,
  onScrollerVisible,
} = useDynamicScroller(dynamicOptions)

function getSlotProps(itemWithSize, index, active) {
  return {
    item: itemWithSize.item,
    index,
    active,
    itemWithSize,
  }
}

defineExpose({
  startSpacerSize,
  endSpacerSize,
  scrollToItem,
  scrollToPosition,
  findItemIndex,
  getItemOffset,
  scrollToBottom,
  getItemSize,
  cacheSnapshot,
  restoreCache,
  forceUpdate,
})
</script>

<template>
  <RecycleScroller
    ref="scroller"
    :items="itemsWithSize.slice()"
    :min-item-size="props.minItemSize"
    :direction="props.direction"
    :cache="props.cache"
    :disable-transform="props.disableTransform"
    :flow-mode="props.flowMode"
    :hidden-position="props.hiddenPosition"
    :enabled="props.enabled"
    :page-mode="props.pageMode"
    key-field="id"
    :list-tag="props.listTag"
    :item-tag="props.itemTag"
    v-bind="$attrs"
    @resize="onScrollerResize"
    @visible="onScrollerVisible"
  >
    <template #default="{ item: itemWithSize, index, active }">
      <slot v-bind="getSlotProps(itemWithSize, index, active)" />
    </template>
    <template v-if="$slots.before" #before>
      <slot name="before" />
    </template>
    <template v-if="$slots.after" #after>
      <slot name="after" />
    </template>
    <template #empty>
      <slot name="empty" />
    </template>
  </RecycleScroller>
</template>
